import { TicketHandoffType } from "@redotech/redo-model/concierge-settings/general";
import { ContentType, MessageType } from "@redotech/redo-model/conversation";
import { ConversationMessage } from "@redotech/redo-model/conversation/conversation-message-db-parser";
import { Conversation } from "@redotech/redo-model/conversation/conversation-schema-db-parser";
import { RedoChatText } from "@redotech/redo-web/arbiter-components/chat/redo-chat-text";
import {
  AddressInformationComponent,
  GetAddressFromSuggestionFn,
  GetAddressSuggestionsFn,
  HandleAddressInformationFn,
} from "@redotech/redo-web/concierge/chat-widget-components/address-information-component";
import { CancelOrderToolMessage } from "@redotech/redo-web/concierge/chat-widget-components/cancel-order-tool-message";
import {
  ContactSupport,
  ContactSupportMode,
} from "@redotech/redo-web/concierge/chat-widget-components/contact-support";
import { CreateBundleToolMessage } from "@redotech/redo-web/concierge/chat-widget-components/create-bundle-tool-message";
import { DiscountCodeBubble } from "@redotech/redo-web/concierge/chat-widget-components/discount-code-bubble";
import { EmailInputBubble } from "@redotech/redo-web/concierge/chat-widget-components/email-input-bubble";
import { OrderLookupToolMessage } from "@redotech/redo-web/concierge/chat-widget-components/order-lookup-tool-message";
import { ProductComparisonTable } from "@redotech/redo-web/concierge/chat-widget-components/product-comparison-table";
import { ProductVisualizationResult } from "@redotech/redo-web/concierge/chat-widget-components/product-visualization-result";
import {
  HandleVisualizationUploadFn,
  ProductVisualizationUpload,
  UploadVisualizationImageFn,
} from "@redotech/redo-web/concierge/chat-widget-components/product-visualization-upload";
import { SubscribeBackInStockToolMessage } from "@redotech/redo-web/concierge/chat-widget-components/subscribe-back-in-stock-tool-message";
import { UpdateOrderToolMessage } from "@redotech/redo-web/concierge/chat-widget-components/update-order-tool-message";
import {
  GetCheckoutUrlFn,
  GetMostRecentOrderIdFn,
  GetOrderDetailsFn,
  GetOrderFn,
  HandleAddToCartCompleteFn,
  HandleCancelOrderFn,
  HandleCreateBundleFn,
  HandleEmailInputFn,
  HandleLiveChatHandoffFn,
  HandleOrderLookupFn,
  HandleTrackOrderFn,
  HandleUpdateOrderFn,
} from "@redotech/redo-web/concierge/common";
import {
  LookupOrderByEmailLoginProps,
  LookupTrackOrder,
} from "@redotech/redo-web/concierge/lookup-track-order";
import { ProductDisplayCarousel } from "@redotech/redo-web/concierge/product-display-carousel";
import { ToolResponseContent } from "@redotech/redo-web/concierge/tool-response-content";
import { SystemMessage } from "@redotech/redo-web/customer-widget/system-message";
import { Flex } from "@redotech/redo-web/flex";
import { UseMutationResult } from "@tanstack/react-query";
import { AddToCartButton } from "../common-components/add-to-cart-button";
import { RecentOrdersSection } from "../views/account/recent-orders-section";

interface BaseMessageRenderOptions {
  index: number;
  readOnly?: boolean;
  storeUrl?: string;
  storefrontAccessToken?: string;
  // Optional interactive handlers - only needed for non-read-only mode
  addToCartHandler?: (params: {
    conversationId: string;
    variantId: number;
    quantity: number;
    actionOriginMessageId: string;
    productTitle?: string;
    productId: string;
  }) => Promise<void>;
  handleSubscribeToBackInStockMutation?: UseMutationResult<
    void,
    Error,
    {
      conversationId: string;
      messageId: string;
      customerEmail: string;
      pendingToolCallId?: string;
    },
    unknown
  >;
  handleResendReturnLabelMutation?: UseMutationResult<
    void,
    Error,
    {
      conversationId: string;
      orderId: string;
      orderEmail: string;
      toolMessageId: string;
    },
    unknown
  >;
  getCheckoutUrl?: GetCheckoutUrlFn;
  lookupOrderByEmailLoginProps?: LookupOrderByEmailLoginProps;
  getAddressSuggestions?: GetAddressSuggestionsFn;
  getAddressFromSuggestion?: GetAddressFromSuggestionFn;
  handoffType?: TicketHandoffType;
  userActionHandlers?: {
    handleAddToCart?: HandleAddToCartCompleteFn;
    handleSubscribeToBackInStock?: HandleEmailInputFn;
    handleContactSupport?: HandleEmailInputFn;
    handleResendReturnLabel?: HandleOrderLookupFn;
    handleTrackOrder?: HandleTrackOrderFn;
    handleLiveChatHandoff?: HandleLiveChatHandoffFn;
    getMostRecentOrderId?: GetMostRecentOrderIdFn;
    handleCancelOrder?: HandleCancelOrderFn;
    getOrder?: GetOrderFn;
    handleAddressInformation?: HandleAddressInformationFn;
    getOrderDetails?: GetOrderDetailsFn;
    handleUpdateOrder?: HandleUpdateOrderFn;
    handleCreateBundle?: HandleCreateBundleFn;
    handleVisualizationUpload?: HandleVisualizationUploadFn;
    uploadVisualizationImage?: UploadVisualizationImageFn;
  };
  handleTrackOrder?: (
    messageId: string,
  ) => (email: string, orderId: string) => Promise<void>;
}

export interface MessageRenderOptions extends BaseMessageRenderOptions {
  conversation: Conversation;
  message: ConversationMessage;
  conversationId?: string;
  messageId?: string;
  filteredMessages: ConversationMessage[];
  findActionableMessage?: (
    toolCallId?: string | null,
  ) => ConversationMessage | undefined;
}

export function renderMessageContent(
  options: MessageRenderOptions,
): JSX.Element {
  const {
    conversation,
    message,
    index,
    filteredMessages,
    readOnly = false,
    addToCartHandler,
    handleResendReturnLabelMutation,
    handleSubscribeToBackInStockMutation,
    storeUrl,
    handoffType,
    storefrontAccessToken,
    lookupOrderByEmailLoginProps,
    getAddressSuggestions,
    getAddressFromSuggestion,
    userActionHandlers,
    findActionableMessage,
    handleTrackOrder,
  } = options;
  const isResponder = message.type !== MessageType.CUSTOMER;
  // Handle TOOL messages
  if (message.type === MessageType.TOOL && findActionableMessage) {
    return (
      <ToolResponseContent
        findActionableMessage={findActionableMessage}
        message={message}
      />
    );
  }

  // Handle SYSTEM messages
  if (message.type === MessageType.SYSTEM) {
    return <SystemMessage>{message.content}</SystemMessage>;
  }

  // Handle different content types
  switch (message.contentType) {
    case ContentType.CART_ITEMS: {
      return (
        <Flex justify="flex-start">
          <AddToCartButton
            addToCartHandler={addToCartHandler}
            conversationId={conversation._id}
            message={message}
            readOnly={readOnly}
          />
        </Flex>
      );
    }

    case ContentType.PRODUCT_DISPLAY: {
      return (
        <ProductDisplayCarousel
          handleAddToCart={async ({
            productId,
            variantId,
            productTitle,
          }: {
            productId: string;
            variantId: string;
            productTitle?: string;
          }) => {
            const quantity = 1;
            await addToCartHandler?.({
              conversationId: conversation._id,
              variantId: +variantId,
              quantity,
              actionOriginMessageId: message._id,
              productId,
              productTitle,
            });
          }}
          message={message}
          readOnly={readOnly}
          storefrontAccessToken={storefrontAccessToken}
          storeUrl={storeUrl}
        />
      );
    }

    case ContentType.TRACK_ORDER_UI: {
      return (
        <LookupTrackOrder
          getMostRecentOrderId={userActionHandlers?.getMostRecentOrderId}
          handleTrackOrder={handleTrackOrder?.(message._id)}
          lookupOrderByEmailLoginProps={lookupOrderByEmailLoginProps}
          message={message}
          storeUrl={storeUrl}
        />
      );
    }

    case ContentType.EMAIL_INPUT: {
      return <EmailInputBubble message={message} />;
    }

    case ContentType.SUBSCRIBE_TO_BACK_IN_STOCK_NOTIFICATION_UI: {
      return (
        <SubscribeBackInStockToolMessage
          buttonText="Subscribe"
          conversationId={conversation._id}
          message={message}
          onEmailEntry={handleSubscribeToBackInStockMutation?.mutateAsync}
          readOnly={readOnly}
        />
      );
    }

    case ContentType.CONTACT_SUPPORT_UI: {
      const finalHandoffType = handoffType ?? TicketHandoffType.EMAIL;

      const mode: ContactSupportMode =
        finalHandoffType === TicketHandoffType.EMAIL ? "email" : "live_chat";

      const buttonText = mode === "live_chat" ? "Connect" : "Submit";

      return (
        <ContactSupport
          buttonText={buttonText}
          conversationId={conversation._id}
          message={message}
          mode={mode}
          onEmailEntry={userActionHandlers?.handleContactSupport}
          onLiveChatHandoff={userActionHandlers?.handleLiveChatHandoff}
          readOnly={readOnly}
        />
      );
    }

    case ContentType.DISCOUNT_CODE: {
      return <DiscountCodeBubble message={message} readOnly={readOnly} />;
    }

    case ContentType.RESEND_RETURN_LABEL_UI: {
      return (
        <OrderLookupToolMessage
          buttonText="Resend return label"
          conversationId={conversation._id}
          lookupInfo={
            message.structuredContent
              ? {
                  orderEmail: message.structuredContent.orderEmail ?? undefined,
                  orderId: message.structuredContent.orderId ?? undefined,
                }
              : undefined
          }
          onOrderLookupRequest={handleResendReturnLabelMutation?.mutateAsync}
          readOnly={readOnly}
          toolMessageId={message._id}
        />
      );
    }

    case ContentType.CANCEL_ORDER: {
      return (
        <CancelOrderToolMessage
          conversationId={conversation._id.toString()}
          getOrderDetails={userActionHandlers?.getOrderDetails}
          message={message}
          onCancelOrder={userActionHandlers?.handleCancelOrder}
          readOnly={readOnly}
        />
      );
    }

    case ContentType.UPDATE_ADDRESS: {
      return (
        <AddressInformationComponent
          addressInfo={message.structuredContent}
          buttonText="Update address"
          conversationId={conversation._id}
          getAddressFromSuggestion={getAddressFromSuggestion}
          getAddressSuggestions={getAddressSuggestions}
          message={message}
          onAddressInformationSubmit={
            userActionHandlers?.handleAddressInformation
          }
          readOnly={readOnly}
          toolMessageId={message._id}
        />
      );
    }

    case ContentType.PRODUCT_COMPARISON: {
      return <ProductComparisonTable comparison={message.structuredContent} />;
    }

    case ContentType.UPDATE_ORDER: {
      return (
        <UpdateOrderToolMessage
          conversationId={conversation._id}
          getOrderDetails={userActionHandlers?.getOrderDetails}
          message={message}
          onUpdateOrder={userActionHandlers?.handleUpdateOrder}
          readOnly={readOnly}
          storefrontAccessToken={storefrontAccessToken}
          storeUrl={storeUrl}
        />
      );
    }

    case ContentType.CREATE_BUNDLE: {
      return (
        <CreateBundleToolMessage
          conversationId={conversation._id}
          message={message}
          onCreateBundle={userActionHandlers?.handleCreateBundle}
          readOnly={readOnly}
          storefrontAccessToken={storefrontAccessToken}
          storeUrl={storeUrl}
        />
      );
    }
    case ContentType.PRODUCT_VISUALIZATION_UPLOAD_UI: {
      if (!userActionHandlers?.uploadVisualizationImage) {
        return <div>Not supported</div>;
      }

      return (
        <ProductVisualizationUpload
          conversationId={conversation._id}
          message={message as any}
          onImageUpload={userActionHandlers?.handleVisualizationUpload}
          readOnly={readOnly}
          uploadImage={userActionHandlers.uploadVisualizationImage}
        />
      );
    }

    case ContentType.PRODUCT_VISUALIZATION_RESULT: {
      return (
        <ProductVisualizationResult
          message={message as any}
          readOnly={readOnly}
        />
      );
    }

    default: {
      // Default text message rendering with optional animation logic
      const now = Temporal.Now.instant();
      const oneMinuteAgo = now.subtract({ minutes: 1 });

      const isNewish = message.sentAt
        ? Temporal.Instant.compare(
            oneMinuteAgo,
            Temporal.Instant.from(new Date(message.sentAt).toISOString()),
          ) < 0
        : false;
      const shouldAnimate =
        !readOnly && // Only animate in interactive mode
        index === filteredMessages.length - 1 &&
        isNewish &&
        isResponder;

      return (
        <Flex dir="column" w="full">
          <RedoChatText
            chatBlock={[message.content]}
            files={message.files?.map((file) => file.url)}
            htmlContent={message.chatFlow?.htmlContent}
            isResponder={isResponder}
            shouldAnimate={shouldAnimate}
            timestamp={new Date(message.sentAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          />
          {(message as any).orderList && (
            <Flex
              borderColor="secondary"
              borderStyle="solid"
              borderWidth="1px"
              mt="sm"
              p="md"
              radius="sm"
              style={{
                backgroundColor:
                  "var(--redo-component-colors-components-buttons-primary-button-primary-bg)",
                backgroundImage: `linear-gradient(
                  170deg,
                  rgba(255, 255, 255, 0.15),
                  rgba(0, 0, 0, 0.15)
                )`,
              }}
            >
              <RecentOrdersSection />
            </Flex>
          )}
        </Flex>
      );
    }
  }
}
